<template>
  <v-form v-if="formData" ref="form" v-model="valid">
    <v-card>
      <v-card-title>
        {{ $t("profileForm.common.title") }}
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.firstname"
              :label="$t('profileForm.common.firstname.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.lastname"
              :label="$t('profileForm.common.lastname.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.email"
              :label="$t('profileForm.common.email.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              type="email"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-combobox
              v-model="formData.emails"
              :label="$t('profileForm.common.emails.label')"
              :rules="[
                (v) => {
                  for (let i = 0; i < v.length; i++) {
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v[i])) return $t('validation.email.invalid');
                  }
                  return true;
                },
              ]"
              multiple
              small-chips
              clearable
              deletable-chips
            >
            </v-combobox>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-title>
        {{ $t("profileForm.company.title") }}
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.name"
              :label="$t('profileForm.company.name.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.contactPerson"
              :label="$t('profileForm.company.contactPerson.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.address"
              :label="$t('profileForm.company.address.address.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.zip"
              :label="$t('profileForm.company.address.zip.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.city"
              :label="$t('profileForm.company.address.city.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="formData.company.address.country"
              :items="countries"
              :label="$t('profileForm.company.address.country.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              clearable
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.vatId"
              :label="$t('profileForm.company.vatId.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.phone"
              :label="$t('profileForm.company.phone.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.website"
              :label="$t('profileForm.company.website.label')"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.sustainedCapital"
              :label="
                $t('profileForm.company.sustainedCapital.label', {
                  year: new Date().getFullYear(),
                })
              "
              :rules="[(v) => !!v || $t('validation.required')]"
              type="number"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              v-model="formData.company.address.totalCapital"
              :label="
                $t('profileForm.company.totalCapital.label', {
                  year: new Date().getFullYear(),
                })
              "
              :rules="[(v) => !!v || $t('validation.required')]"
              type="number"
              required
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="formData.company.address.currency"
              :items="currencies"
              :label="$t('fundBaseForm.currency.label')"
              :rules="[(v) => !!v || $t('validation.required')]"
            >
            </v-autocomplete>
          </v-col>

          <v-col cols="12" md="6">
            <v-select
              :items="languages"
              v-model="formData.company.address.billingLanguage"
              :label="$t('fundBaseForm.company.billingLanguage.label')"
            />
          </v-col>

          <v-col cols="12">
            <v-checkbox
              v-model="formData.useBillingAddress"
              :label="$t('fundBaseForm.useBillingAddress.label')"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-card-text>

      <template v-if="formData.useBillingAddress">
        <v-card-title>
          {{ $t("profileForm.company.billingAddress.title") }}
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.company.billingAddress.name"
                :label="$t('profileForm.company.name.label')"
                :rules="[(v) => !!v || $t('validation.required')]"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.company.billingAddress.contactPerson"
                :label="$t('profileForm.company.contactPerson.label')"
                :rules="[(v) => !!v || $t('validation.required')]"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.company.billingAddress.address"
                :label="$t('profileForm.company.billingAddress.address.label')"
                :rules="[(v) => !!v || $t('validation.required')]"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.company.billingAddress.zip"
                :label="$t('profileForm.company.billingAddress.zip.label')"
                :rules="[(v) => !!v || $t('validation.required')]"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.company.billingAddress.city"
                :label="$t('profileForm.company.billingAddress.city.label')"
                :rules="[(v) => !!v || $t('validation.required')]"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="formData.company.billingAddress.country"
                :items="countries"
                :label="$t('profileForm.company.billingAddress.country.label')"
                :rules="[(v) => !!v || $t('validation.required')]"
                clearable
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.company.billingAddress.vatId"
                :label="$t('profileForm.company.vatId.label')"
                :rules="[(v) => !!v || $t('validation.required')]"
                required
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </template>

      <v-card-text v-if="!valid">
        <v-alert type="info">
          {{ $t("profileForm.notReadyToFinish") }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="formData.useBillingAddress"
          :disabled="!valid"
          color="primary"
          class="mr-4"
          @click="update"
          :loading="loading"
        >
          {{ $t("profileForm.save") }}
        </v-btn>

        <v-dialog v-model="acceptDialog" persistent max-width="600" v-if="!formData.useBillingAddress">
          <template #activator="{ on, attrs }">
            <v-btn :disabled="!valid" color="primary" class="mr-4" v-bind="attrs" v-on="on">
              {{ $t("profileForm.save") }}
            </v-btn>
          </template>

          <v-card>
            <v-card-title class="headline">
              {{ $t("profileForm.confirmation.title") }}
            </v-card-title>

            <v-card-text>
              <Markdown :value="$t('profileForm.confirmation.description')" />
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>

              <v-btn
                color="warning"
                @click="
                  formData.useBillingAddress = true;
                  acceptDialog = false;
                "
              >
                {{ $t("profileForm.confirmation.cancel") }}
              </v-btn>

              <v-btn color="primary" @click="update" :loading="loading">
                {{ $t("profileForm.confirmation.accept") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-actions>

      <v-snackbar v-model="saved" color="secondary">
        <div class="text-center flex-grow-1">
          {{ $t("action.saved") }}
        </div>
      </v-snackbar>
    </v-card>
  </v-form>
</template>

<script>
import { sortBy, prop } from "ramda";
import countries from "@fng/common/countries";
import currencies from "@fng/common/currencies";
import generateSelectItems from "@/utils/generateSelectItems";
import ProfileQuery from "@/gql/queries/ProfileQuery.gql";
import UpdateCurrentUserMutation from "@/gql/mutations/UpdateCurrentUserMutation.gql";

const languages = ["de", "en"];

export default {
  data() {
    return {
      formData: null,
      currentUser: null,
      valid: true,
      loading: false,
      saved: false,
      acceptDialog: false,
    };
  },

  apollo: {
    currentUser: {
      query: ProfileQuery,
      result({ data }) {
        if (!this.formData) this.setFormdata(data.currentUser);
      },
    },
  },

  computed: {
    currencies() {
      return sortBy(prop("text"), generateSelectItems(currencies, "currencies").apply(this));
    },

    countries() {
      return sortBy(prop("text"), generateSelectItems(countries, "countries").apply(this));
    },

    languages: generateSelectItems(languages, "languages"),
  },

  methods: {
    setFormdata({ firstname, lastname, email, emails, company, proceduralConditions, useBillingAddress }) {
      this.formData = {
        firstname,
        lastname,
        email,
        emails,
        proceduralConditions,
        useBillingAddress,
        company: {
          address: {
            name: company.address.name,
            contactPerson: company.address.contactPerson,
            phone: company.address.phone,
            website: company.address.website,
            sustainedCapital: company.address.sustainedCapital,
            totalCapital: company.address.totalCapital,
            currency: company.address.currency,
            vatId: company.address.vatId,
            billingLanguage: company.address.billingLanguage,
            address: company.address.address,
            zip: company.address.zip,
            city: company.address.city,
            country: company.address.country,
          },
          billingAddress: {
            name: company.billingAddress.name,
            contactPerson: company.billingAddress.contactPerson,
            address: company.billingAddress.address,
            zip: company.billingAddress.zip,
            city: company.billingAddress.city,
            country: company.billingAddress.country,
            vatId: company.billingAddress.vatId,
          },
        },
      };
    },

    async update() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      const { formData } = this;

      formData.company.address.sustainedCapital = parseFloat(formData.company.address.sustainedCapital);

      formData.company.address.totalCapital = parseFloat(formData.company.address.totalCapital);

      await this.$apollo.mutate({
        mutation: UpdateCurrentUserMutation,
        variables: {
          input: formData,
        },
      });

      this.loading = false;
      this.saved = true;
      this.acceptDialog = false;
    },
  },
};
</script>
